import type { FileExtension } from '../../utils/fileSharingUrlUtils';
import type { MakeStylesHook, StyleRules } from '@egr/xbox/utils/tss-react';

import * as React from 'react';

import { appStore } from '../../stores/AppStore';

import { developmentMode } from '@egr/xbox/utils/ReactScriptHelper';
import { makeStyles } from '@egr/xbox/utils/tss-react';

function getStyles(): StyleRules<'overlay' | 'content' | 'title' | 'subtitle'> {
    return {
        overlay: {
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
        },
        content: {
            width: 'min(560px, calc(100% - 48px))',
            border: '2px dashed #f7a600',
            borderRadius: 12,
            backgroundColor: '#fff',
            padding: '28px 24px',
            textAlign: 'center',
        },
        title: {
            margin: 0,
            fontSize: 22,
            lineHeight: 1.2,
            color: '#333',
        },
        subtitle: {
            marginTop: 10,
            color: '#666',
            fontSize: 14,
        },
    };
}

const useStyles: MakeStylesHook<'overlay' | 'content' | 'title' | 'subtitle'> = makeStyles({ name: 'LocalFileDropTarget' })(getStyles);

function getExtension(name: string): FileExtension | undefined {
    const ext = name.split('.').pop()?.toLowerCase() as FileExtension | undefined;
    return ext;
}

export const LocalFileDropTarget: React.FC = () => {
    const { classes } = useStyles();
    const [dragging, setDragging] = React.useState(false);
    const dragCounter = React.useRef(0);

    React.useEffect(() => {
        if (!developmentMode) {
            return;
        }

        const onDragEnter = (event: DragEvent) => {
            if (event.dataTransfer?.types.includes('Files')) {
                dragCounter.current += 1;
                setDragging(true);
            }
        };

        const onDragLeave = () => {
            dragCounter.current = Math.max(0, dragCounter.current - 1);
            if (dragCounter.current === 0) {
                setDragging(false);
            }
        };

        const onDragOver = (event: DragEvent) => {
            if (event.dataTransfer?.types.includes('Files')) {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
            }
        };

        const onDrop = (event: DragEvent) => {
            if (!event.dataTransfer?.files?.length) {
                return;
            }

            event.preventDefault();
            dragCounter.current = 0;
            setDragging(false);

            const file = event.dataTransfer.files[0];
            const extension = getExtension(file.name);
            if (extension == null) {
                return;
            }

            void appStore.setLocalFile(file);
        };

        window.addEventListener('dragenter', onDragEnter);
        window.addEventListener('dragleave', onDragLeave);
        window.addEventListener('dragover', onDragOver);
        window.addEventListener('drop', onDrop);

        return () => {
            window.removeEventListener('dragenter', onDragEnter);
            window.removeEventListener('dragleave', onDragLeave);
            window.removeEventListener('dragover', onDragOver);
            window.removeEventListener('drop', onDrop);
        };
    }, []);

    if (!developmentMode || !dragging) {
        return null;
    }

    return (
        <div className={classes.overlay}>
            <div className={classes.content}>
                <h2 className={classes.title}>Drop local file to load</h2>
                <div className={classes.subtitle}>Supported: .pec, .pbox, .eimg, .epan, .glb, .gltf, .usdz, .iar, .jpg, .jpeg, .png</div>
            </div>
        </div>
    );
};
